import { useCallback, useEffect, useRef, useState } from "react";
import {
    useDataChannel,
    useLocalParticipant,
    useRoomContext,
} from "@livekit/components-react";
import { ConnectionState, RoomEvent } from "livekit-client";

import {
    MEDITATION_TOPIC,
    MeditationMessage,
    MeditationSessionState,
    mayControlMeditation,
} from "../domain/meditationChannel";

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export type MeditationChannel = {
    /** What is running, on both screens. Null when nothing is. */
    state: MeditationSessionState | null;
    localIdentity: string;
    /** Whether this participant drives it — true for the therapist. */
    canControl: boolean;
    /** True while packets are failing to reach the other side. */
    transportFailed: boolean;
    /** Put a meditation up for both people. */
    startMeditation: (state: Omit<MeditationSessionState, "atEpochMs">) => void;
    /** Tell the other side where it is up to now. */
    publishState: (state: Omit<MeditationSessionState, "atEpochMs">) => void;
    /** Take it down for both people. */
    stopMeditation: () => void;
};

/**
 * Keeping both screens on the same meditation.
 *
 * The audio itself does not travel here — it reaches the patient as a
 * published LiveKit track, so the two of them hear one playback rather than
 * two that drift. What travels here is the state around it: which meditation
 * is up, whether it is running, and where it has got to, so the patient's
 * screen says the same thing as the therapist's.
 *
 * Every message carries the whole state rather than a delta. A dropped packet
 * then costs one stale render instead of leaving the two sides permanently
 * disagreeing, which is the failure that matters when the patient has their
 * eyes closed and the therapist cannot see their screen.
 *
 * Call this above whatever shows the meditation: the panel comes and goes
 * whenever someone glances back at the faces, and unmounting the state with it
 * would end a meditation that is still playing.
 */
export function useMeditationChannel(): MeditationChannel {
    const [state, setState] = useState<MeditationSessionState | null>(null);
    const [transportFailed, setTransportFailed] = useState(false);

    const room = useRoomContext();
    const { localParticipant } = useLocalParticipant();
    const localIdentity = localParticipant?.identity ?? "";
    const canControl = mayControlMeditation(localIdentity);

    const sendRef = useRef<
        ((payload: Uint8Array, options: object) => Promise<void> | void) | null
    >(null);
    // Read from `answerSync`, which is a callback rather than a render.
    const stateRef = useRef<MeditationSessionState | null>(null);
    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    // Only flips on the edge, so a meditation that is playing does not
    // re-render the panel once per position packet.
    const failing = useRef(false);
    const markTransport = useCallback((failed: boolean) => {
        if (failing.current === failed) return;
        failing.current = failed;
        setTransportFailed(failed);
    }, []);

    const publish = useCallback(
        (message: MeditationMessage) => {
            const send = sendRef.current;
            if (!send) return;
            try {
                const result = send(encoder.encode(JSON.stringify(message)), {
                    reliable: true,
                    topic: MEDITATION_TOPIC,
                });
                if (result && typeof result.then === "function") {
                    result.then(
                        () => markTransport(false),
                        () => markTransport(true)
                    );
                }
            } catch {
                markTransport(true);
            }
        },
        [markTransport]
    );

    const onMessage = useCallback(
        (raw: { payload: Uint8Array; from?: { identity: string } }) => {
            let message: MeditationMessage;
            try {
                message = JSON.parse(
                    decoder.decode(raw.payload)
                ) as MeditationMessage;
            } catch {
                // A malformed packet is not worth taking the meditation down for.
                return;
            }

            const senderIdentity = raw.from?.identity ?? "";

            switch (message.kind) {
                case "start":
                case "state":
                    // Enforced on arrival, not only by hiding the controls: a
                    // packet is whatever the other browser chose to send.
                    if (mayControlMeditation(senderIdentity)) setState(message.state);
                    break;
                case "stop":
                    if (mayControlMeditation(senderIdentity)) setState(null);
                    break;
                case "sync-request":
                    // Only the therapist can answer, because only their state
                    // is authoritative — and answering with `null` matters as
                    // much as answering with a meditation, so a patient who
                    // rejoined stops waiting for one that is not running.
                    if (!canControl) return;
                    if (stateRef.current) {
                        publish({ kind: "state", state: stateRef.current });
                    } else {
                        publish({ kind: "stop" });
                    }
                    break;
            }
        },
        [canControl, publish]
    );

    const { send } = useDataChannel(MEDITATION_TOPIC, onMessage);
    useEffect(() => {
        sendRef.current = send as unknown as (
            payload: Uint8Array,
            options: object
        ) => Promise<void>;
    }, [send]);

    /**
     * Ask what is running — on the way in and again after a reconnection.
     *
     * Fired on the room's own connected event rather than on mount: LiveKit
     * renders its children while still connecting, and a request sent then is
     * never delivered, which is how a patient who dropped out comes back to a
     * blank screen while a meditation is still playing in their ears.
     */
    useEffect(() => {
        if (!room || canControl) return;

        const request = () => publish({ kind: "sync-request" });
        if (room.state === ConnectionState.Connected) request();

        room.on(RoomEvent.Connected, request);
        room.on(RoomEvent.Reconnected, request);
        return () => {
            room.off(RoomEvent.Connected, request);
            room.off(RoomEvent.Reconnected, request);
        };
    }, [canControl, publish, room]);

    const stamp = useCallback(
        (next: Omit<MeditationSessionState, "atEpochMs">): MeditationSessionState => ({
            ...next,
            atEpochMs: Date.now(),
        }),
        []
    );

    const startMeditation = useCallback(
        (next: Omit<MeditationSessionState, "atEpochMs">) => {
            if (!canControl) return;
            const stamped = stamp(next);
            setState(stamped);
            publish({ kind: "start", state: stamped });
        },
        [canControl, publish, stamp]
    );

    const publishState = useCallback(
        (next: Omit<MeditationSessionState, "atEpochMs">) => {
            if (!canControl) return;
            const stamped = stamp(next);
            setState(stamped);
            publish({ kind: "state", state: stamped });
        },
        [canControl, publish, stamp]
    );

    const stopMeditation = useCallback(() => {
        if (!canControl) return;
        setState(null);
        publish({ kind: "stop" });
    }, [canControl, publish]);

    return {
        state,
        localIdentity,
        canControl,
        transportFailed,
        startMeditation,
        publishState,
        stopMeditation,
    };
}
